/* oxlint-disable max-lines-per-function */
import clsx from 'clsx';
import { type BaseUser, getCohort, getPreferredName } from 'Users';
import { type TFunction } from 'i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { memo, useCallback, useEffect, useState, type RefObject, createRef, useMemo, useRef, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { useEventLogger } from 'FrontRoyalAngular';
import { type AnyObject } from '@Types';
import { type ToggleAudioOrTextConfig, type BotUiContext } from 'TutorBotConversation';
import { useTutorBotBottomSpacing } from '../hooks/useTutorBotBottomSpacing';
import { ToggleAudioOrTextButton } from '../ToggleAudioOrTextButton/ToggleAudioOrTextButton';
import { type OutgoingMessage, type PretendAiMessage } from '../types/ChatMessage.types';
import { type BotClientContext } from '../types/Context.types';
import {
    chatActions,
    getActiveConversationId,
    getHasChangedTopic,
    getAiMessageLoading,
    getActiveConversationMessages,
    getErroredAiMessage,
} from '../redux/chat';
import { createPretendAiMessage, isPretendAiMessage } from '../utils/ChatMessage';
import { useConversation } from '../hooks/useConversation';
import { type MessageFormValues } from '../types/OtherInternalTypes.types';
import { type GreetingMessageConfig } from '../types/GreetingMessageConfig.types';
import { type TutorBotChatStatusConfig } from '../types/TutorBotChatStatusConfig.types';
import { ClearButton } from '../MessageForm/ClearButton';
import { GreetingMessage } from '../GreetingMessage';
import { Message } from '../Message/Message';
import { MESSAGE_INPUT_MAX_LENGTH } from '../constants';
import { MessageForm } from '../MessageForm/MessageForm';
import { useScrollToBottomOfChat } from '../hooks/useScrollToBottomOfChat';
import { WelcomeModal } from '../WelcomeModal/WelcomeModal';
import { ChatContextProvider, useChatContext } from './ChatContext';
import { type StructuredMessageHandler } from '../types/StructuredMessage.types';

const SCROLL_HAZE_BASE_COLOR = 'var(--chat-scroll-haze-color, rgba(251, 251, 251, 1))';

type ErrorMessageFactory = (conversationId: string, t: TFunction) => PretendAiMessage;
const getErrorMessage: ErrorMessageFactory = (conversationId: string, t: TFunction) =>
    createPretendAiMessage({
        content: t('chat.chat.error'),
        id: 'error-message',
        conversationId,
    });

type ChatControlsConfig = {
    // Callers can pass in a container if they want the chat controlers (the user input) placed
    // somewhere else on the screen other than inside the <Chat> component itself. We use this
    // for the FreeResponse frame in the lesson player.
    container?: HTMLElement | null;

    // FreeResponse might end up placing the chat controls container over the bottom of this element,
    // so it needs the ability to configure the bottom padding of the chat messages so we can scroll
    // all the messages into view.
    xsBottomPadding?: number | null;
};

const XS_BOTTOM_PADDING_CLASS_NAMES = new Map<number | null, string>([
    [12.5, 'pb-12.5 sm:pb-2.5'],
    [null, 'pb-2.5'],
]);

type Props = {
    currentUser: BaseUser;
    clientContext: BotClientContext;
    tutorBotStatusConfig: TutorBotChatStatusConfig;
    greetingMessageConfig?: GreetingMessageConfig;
    botName: string;
    disable?: boolean;

    // See comment near the BaseMessage type about what it means to be an "initial" message
    initialMessage?: OutgoingMessage | PretendAiMessage;
    endConversationOnClose?: boolean;
    enableSetNewTopic?: boolean;
    supportTab?: boolean;
    welcomeModalClassname?: string;
    toggleAudioOrTextConfig?: ToggleAudioOrTextConfig;
    chatControls?: ChatControlsConfig;
    onStructuredMessage?: StructuredMessageHandler;
    onMessageTextChange?: (text: string) => void;
    scrollElement?: Element | null;

    openToggleAudioOrTextTooltip?: boolean; // storybook only
    showCanMakeMistakesMessage?: boolean;
};

function useLogLaunchEvent(uiContext: BotUiContext) {
    const EventLogger = useEventLogger();

    useEffect(() => {
        EventLogger.log('tutorbot:launched_text_chat', { label: uiContext, ui_context: uiContext });
    }, [uiContext, EventLogger]);
}

function parseChatControlsConfig(chatControls: ChatControlsConfig | undefined | null): {
    container: HTMLElement | null;
    messagesPaddingClassName: string;
} {
    const container = chatControls?.container ?? null;
    const xsBottomPadding = container ? (chatControls?.xsBottomPadding ?? null) : null;
    const messagesPaddingClassName =
        XS_BOTTOM_PADDING_CLASS_NAMES.get(xsBottomPadding ?? null) ?? XS_BOTTOM_PADDING_CLASS_NAMES.get(null)!;
    return { container, messagesPaddingClassName };
}

function ChatComponent({
    currentUser,
    botName,
    clientContext: currentClientContext,
    initialMessage: currentInitialMessage,
    endConversationOnClose = false,
    enableSetNewTopic = false,
    supportTab = false,
    toggleAudioOrTextConfig,
    openToggleAudioOrTextTooltip = undefined,
    disable: forceDisable = false,
    chatControls,
    onStructuredMessage,
    onMessageTextChange,
    showCanMakeMistakesMessage = true,
    scrollElement,
}: Props): JSX.Element {
    const { container: chatControlsContainer = null, messagesPaddingClassName } = parseChatControlsConfig(chatControls);
    const dispatch = useDispatch();
    const hasChangedTopic = useSelector(getHasChangedTopic);
    const { t } = useTranslation('back_royal');
    const activeConversationId = useSelector(getActiveConversationId);
    const aiMessageLoading = useSelector(getAiMessageLoading);
    const spacingBeneathLowestButton = useTutorBotBottomSpacing();
    const messageRefs = useRef<AnyObject<RefObject<HTMLDivElement>>>({});
    const chatHistory = useSelector(getActiveConversationMessages);
    const messagesWithRefs = useMemo(
        () =>
            chatHistory.map(message => {
                messageRefs.current[message.id] ||= createRef<HTMLDivElement>() as RefObject<HTMLDivElement>;

                return {
                    message,
                    ref: messageRefs.current[message.id],
                };
            }),
        [chatHistory],
    );
    useLogLaunchEvent(currentClientContext.uiContext);

    const erroredAiMessage = useSelector(getErroredAiMessage);

    // clientContext, initialMessage, and user are only used on initialization, so we
    // don't pay attention to changes to the parameters after the component is rendered
    const [clientContext] = useState(() => currentClientContext);
    const [rawInitialMessage] = useState(() => currentInitialMessage);
    const [user] = useState(() => currentUser);
    const cohort = getCohort(currentUser);

    const { tutorBotStatusConfig } = useChatContext();
    const isLocked = forceDisable ? true : tutorBotStatusConfig.isLocked;

    // If the lesson player launches AI Tutor when it is locked (which, at least today, would only be
    // because DISABLE_TUTOR_BOT is true), then we don't send the initial message.
    let initialMessage: OutgoingMessage | PretendAiMessage | undefined | null = rawInitialMessage;
    if (initialMessage && isLocked) {
        initialMessage = null;
    }

    const userName = getPreferredName(user);

    const { scrollWrapperRef, chatMessagesContainerRef } = useScrollToBottomOfChat({ messagesWithRefs, scrollElement });

    const { retry, askUserMessage, startNewConversation, cancelCurrentStreamingMessage } = useConversation(
        clientContext,
        cohort?.id || null,
        initialMessage,
        endConversationOnClose,
        onStructuredMessage,
    );
    //---------------------------
    // Conversations
    //---------------------------

    const handleSubmit = useCallback(
        (data: MessageFormValues) => {
            if (!data?.message) return;
            askUserMessage({ content: data.message, role: 'human' }, supportTab);
            dispatch(chatActions.setHasChangedTopic({ hasChangedTopic: false }));
        },
        [askUserMessage, dispatch, supportTab],
    );

    const handleNewTopicClick = useCallback(() => {
        if (hasChangedTopic) return;

        // Reset the scroll to the top in order to ensure
        // the scrollToBottomOfChat animates the sole GreetingMessage
        // into view when a user starts a new topic.
        if (scrollWrapperRef?.current) {
            scrollWrapperRef.current.scrollTop = 0;
        }

        startNewConversation();
        dispatch(chatActions.setHasChangedTopic({ hasChangedTopic: true }));
    }, [hasChangedTopic, startNewConversation, dispatch, scrollWrapperRef]);

    const handleCancelClick = useCallback(() => {
        cancelCurrentStreamingMessage('by_user');
    }, [cancelCurrentStreamingMessage]);

    const disableInput = aiMessageLoading || isLocked;

    // When we're showing the microphone button to toggle to voice, there's no
    // space for the character count
    const showToggleAudioOrTextButton =
        toggleAudioOrTextConfig?.canLaunchReviewPreviousMaterialAudio ||
        toggleAudioOrTextConfig?.showDisabledVoiceChatButtonReason;
    const hideCharacterCount = !!showToggleAudioOrTextButton;

    const renderChatControls = (isPortal: boolean) => {
        const controlsClassName = clsx(
            isPortal ? 'relative w-full' : 'mb relative grow-0 pt-1',
            !isPortal && supportTab && 'px-7.5',
        );
        const shouldRenderScrollHaze = !isPortal;
        const scrollHazeStyle = {
            background: `linear-gradient(to top, ${SCROLL_HAZE_BASE_COLOR} 0%, ${SCROLL_HAZE_BASE_COLOR} 80%, transparent 100%)`,
        };

        return (
            <div data-testid="chat-controls" className={controlsClassName}>
                <div data-id="chat-input-wrapper" className={clsx('relative z-10')}>
                    <div className="relative flex items-stretch gap-[14px]">
                        {/* desktop-only new topic button */}
                        {enableSetNewTopic && (
                            <ClearButton
                                className="hidden basis-auto self-end sm:flex"
                                onClick={handleNewTopicClick}
                                isLocked={isLocked}
                            />
                        )}
                        {toggleAudioOrTextConfig && (
                            <ToggleAudioOrTextButton
                                toggleAudioOrTextConfig={toggleAudioOrTextConfig}
                                openToggleAudioOrTextTooltip={openToggleAudioOrTextTooltip}
                            />
                        )}
                        <MessageForm
                            onSubmit={handleSubmit}
                            className="flex-1"
                            maxLength={MESSAGE_INPUT_MAX_LENGTH}
                            disableInput={disableInput}
                            onCancelClick={handleCancelClick}
                            canCancel={aiMessageLoading}
                            hideCharacterCount={hideCharacterCount}
                            onMessageChange={onMessageTextChange}
                        />
                    </div>
                    {showCanMakeMistakesMessage && (
                        <div
                            // This is absolutely positioned so that it doesn't interfere with the bottom margin of the input,
                            // which has to match another style (see comment in chat-input-wrapper element above)
                            className={clsx(
                                'absolute w-full pt-[10px] text-center text-[10px] text-slate-grey',
                                disableInput && 'opacity-50',
                            )}
                        >
                            {t('chat.chat.bot_can_make_mistakes', { botName })}
                        </div>
                    )}
                </div>
                {shouldRenderScrollHaze && (
                    <div
                        data-testid="scroll-haze"
                        className="absolute bottom-0 left-0 z-0 h-[115%] w-full sm:h-[125%]"
                        style={scrollHazeStyle}
                    />
                )}
                {!isPortal && <div className={spacingBeneathLowestButton} />}
            </div>
        );
    };

    return (
        <>
            <div className="absolute left-0 right-0 top-5 z-20 flex flex-col items-center">
                {/* mobile-only new topic button */}
                {enableSetNewTopic && (
                    <ClearButton
                        className="mb-5 basis-auto shadow-smallish sm:hidden"
                        onClick={handleNewTopicClick}
                        isLocked={isLocked}
                    />
                )}
                <WelcomeModal />
            </div>
            <div className={clsx('flex h-full min-h-0 w-full flex-col')}>
                <div className={clsx('min-h-0 grow overflow-y-hidden', { 'px-7.5': supportTab })}>
                    <div
                        ref={scrollWrapperRef}
                        className="h-full w-full overflow-y-auto pb-3 pt-4 scrollbar-hide sm:pb-0"
                        data-testid="chat-window"
                    >
                        <div
                            data-testid="chat-messages-container"
                            className={clsx('flex min-h-full flex-col justify-end', messagesPaddingClassName)}
                            ref={chatMessagesContainerRef}
                        >
                            {activeConversationId && <GreetingMessage name={userName} />}

                            {messagesWithRefs.map(
                                ({ message, ref }, i) =>
                                    !message.hasError &&
                                    !message.hidden && (
                                        <Message
                                            message={message}
                                            key={message.id}
                                            // We are trying to avoid showing the fake spinner again when the
                                            // chat window is closed and re-opened. This logic works to accomplish
                                            // that if there are any messages after the pretend message, which
                                            // is good enough and keeps the logic simple.
                                            showPretendLoadingSpinner={
                                                isPretendAiMessage(message) && i === chatHistory.length - 1
                                            }
                                            messageRef={ref}
                                        />
                                    ),
                            )}

                            {erroredAiMessage && activeConversationId && (
                                <Message retryClick={retry} message={getErrorMessage(activeConversationId, t)} />
                            )}
                        </div>
                    </div>
                </div>
                {!chatControlsContainer && renderChatControls(false)}
            </div>
            {chatControlsContainer && createPortal(renderChatControls(true), chatControlsContainer)}
        </>
    );
}

// We need a wrapper element so that the loading boundary can capture errors thrown while
// setting up the tutorBotStatusConfig, while that config is still accessible to the element
function Wrapper(props: Props) {
    const { tutorBotStatusConfig, welcomeModalClassname, greetingMessageConfig } = props;
    return (
        <ChatContextProvider
            tutorBotStatusConfig={tutorBotStatusConfig}
            greetingMessageConfig={greetingMessageConfig}
            welcomeModalClassname={welcomeModalClassname}
        >
            <ChatComponent {...props} />
        </ChatContextProvider>
    );
}

export const Chat = memo(Wrapper) as typeof Wrapper;

export default Chat;
